
import React, { useEffect, useState, useLayoutEffect, useMemo } from "react";
import moment from "moment";
import { titleDayArray, generateDaysArrayForEetdagboek,classNames } from "./calendarUtils";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";


const CalendarHeader = ({ currentDate, onPrevious, onNext, setView }) => (
  <header className="flex flex-wrap items-center justify-between gap-3 border-b border-wa-border bg-wa-panel px-3 py-3 sm:px-5">
    <button
      type="button"
      onClick={onPrevious}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text"
      aria-label="Vorige maand"
    >
      <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
    </button>

    <h1 className="text-base font-semibold capitalize leading-6 tracking-wide text-wa-text sm:text-lg">
      {moment(currentDate).locale("nl-be").format("MMMM YYYY")}
    </h1>

    <button
      type="button"
      onClick={onNext}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-wa-muted transition-colors hover:bg-wa-hover hover:text-wa-text"
      aria-label="Volgende maand"
    >
      <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
    </button>

    <button
      type="button"
      onClick={() => setView(1)}
      className="ml-auto rounded-lg bg-wa-accent px-3 py-2 text-sm font-semibold text-wa-onaccent transition-colors hover:brightness-110 sm:ml-3"
    >
      <span className="hidden sm:inline">Eetdagboek View</span>
      <span className="sm:hidden">Terug</span>
    </button>
  </header>
);

const CalendarDayCell = ({ day, onShowEvent, compact = false }) => {
  const weekday = moment(day?.date).isoWeekday();
  const isWeekend = weekday === 6 || weekday === 7;

  return (
    <div
      className={classNames(
        compact
          ? "relative min-h-[82px] border-t border-wa-border p-1.5 transition-colors"
          : "relative min-h-[96px] border-t border-wa-border p-2 transition-colors sm:min-h-[120px] sm:p-2.5 lg:min-h-[130px]",
        day?.isCurrentMonth
          ? isWeekend
            ? "bg-wa-panel2/90"
            : "bg-wa-panel2"
          : "bg-wa-panel text-wa-muted/60",
        day?.isToday ? "ring-1 ring-wa-accent/40 ring-inset" : "hover:bg-wa-rowhover"
      )}
    >
      <time
        dateTime={day?.date}
        className={classNames(
          "inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold",
          day?.isToday
            ? "bg-wa-accent text-wa-onaccent"
            : day?.isCurrentMonth
            ? "text-wa-text"
            : "text-wa-muted"
        )}
      >
        {day?.date?.split("-").pop().replace(/^0/, "")}
      </time>
      {day?.events?.length > 0 && (
        <ol className="mt-2 space-y-1">
          {day?.events?.slice(0, 2).map((event) => (
            <li key={event?.eetdagboek_id}>
              <button
                type="button"
                onClick={() => onShowEvent(event)}
                className="block w-full truncate rounded-md border border-wa-accent/30 bg-wa-accent/12 px-1.5 py-1 text-left text-[11px] font-medium text-wa-text transition-colors hover:bg-wa-accent/20"
                title={event?.eetdagboek_title}
              >
                {event?.eetdagboek_title}
              </button>
            </li>
          ))}
          {day?.events?.length > 2 && (
            <li className="px-1 text-[11px] font-medium text-wa-muted">
              + {day?.events?.length - 2} meer
            </li>
          )}
        </ol>
      )}
    </div>
  );
};

const MonthViewEetdagboek = ({ user, userdetails, appointments, setView }) => {
  const [currentDate, setCurrentDate] = useState(
    moment().locale("nl-be").format("YYYY-MM")
  );
  const [days, setDays] = useState(generateDaysArrayForEetdagboek(currentDate, null, appointments));
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const totalRows = useMemo(
    () => Math.max(5, Math.ceil((days?.length || 35) / 7)),
    [days]
  );
  const visibleDayTitles = useMemo(
    () => (isMobile ? titleDayArray.map((title) => title.slice(0, 2)) : titleDayArray),
    [isMobile]
  );
  const monthEvents = useMemo(
    () =>
      (appointments || [])
        .filter((event) =>
          moment(event?.datetime || event?.eetdagboek_datum).isSame(currentDate, "month")
        )
        .sort((a, b) =>
          moment(a?.datetime || a?.eetdagboek_datum).valueOf() -
          moment(b?.datetime || b?.eetdagboek_datum).valueOf()
        ),
    [appointments, currentDate]
  );

  useLayoutEffect(() => {
    setDays(generateDaysArrayForEetdagboek(currentDate, null, appointments));
  }, [appointments]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const applyMatch = () => setIsMobile(media.matches);
    applyMatch();
    if (media.addEventListener) {
      media.addEventListener("change", applyMatch);
      return () => media.removeEventListener("change", applyMatch);
    }
    media.addListener(applyMatch);
    return () => media.removeListener(applyMatch);
  }, []);

  useEffect(() => {
    setDays(generateDaysArrayForEetdagboek(currentDate, null, appointments));
  }, [currentDate]);

  const handlePreviousMonth = () => {
    setCurrentDate(moment(currentDate).subtract(1, "month").format("YYYY-MM"));
  };

  const handleNextMonth = () => {
    setCurrentDate(moment(currentDate).add(1, "month").format("YYYY-MM"));
  };

  const showEvent = (event) => {
    setSelectedEvent(event);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-wa-border bg-wa-panel2 shadow-lg shadow-black/10">
      <CalendarHeader
        currentDate={currentDate}
        onPrevious={handlePreviousMonth}
        onNext={handleNextMonth}
        setView={setView}
      />

      <div className={classNames(isMobile ? "overflow-x-auto" : "")}>
        <div className={classNames(isMobile ? "min-w-[44rem]" : "")}>
          <div className="grid grid-cols-7 border-b border-wa-border bg-wa-panel text-center text-[11px] font-semibold uppercase tracking-wide text-wa-muted sm:text-xs">
            {visibleDayTitles.map((title, key) => (
              <div key={key} className="border-r border-wa-border py-2 last:border-r-0 sm:py-2.5">
                {title}
              </div>
            ))}
          </div>

          <div
            className="grid grid-cols-7"
            style={{ gridTemplateRows: `repeat(${totalRows}, minmax(${isMobile ? 82 : 96}px, 1fr))` }}
          >
            {days.map((day) => (
              <CalendarDayCell key={day.date} day={day} onShowEvent={showEvent} compact={isMobile} />
            ))}
          </div>
        </div>
      </div>

      {isMobile && (
        <section className="border-t border-wa-border bg-wa-panel px-3 py-3">
          <h3 className="text-sm font-semibold text-wa-text">Ingevulde dagen deze maand</h3>
          {monthEvents.length === 0 ? (
            <p className="mt-2 text-xs text-wa-muted">Nog geen items in deze maand.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {monthEvents.slice(0, 8).map((event) => (
                <li key={event?.eetdagboek_id}>
                  <button
                    type="button"
                    onClick={() => showEvent(event)}
                    className="flex w-full items-center justify-between rounded-lg border border-wa-border bg-wa-panel2 px-3 py-2 text-left hover:border-wa-accent/40"
                  >
                    <span className="truncate text-sm text-wa-text">{event?.eetdagboek_title || "Dagboek"}</span>
                    <span className="ml-3 shrink-0 text-xs font-medium text-wa-muted">
                      {moment(event?.datetime || event?.eetdagboek_datum).format("DD/MM")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {selectedEvent && (
        <div className="border-t border-wa-border bg-wa-panel px-3 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-wa-muted">Geselecteerd</p>
          <p className="mt-1 text-sm font-semibold text-wa-text">
            {selectedEvent?.eetdagboek_title || "Dagboek item"}
          </p>
          <p className="mt-1 text-xs text-wa-muted">
            {moment(selectedEvent?.datetime || selectedEvent?.eetdagboek_datum).format("dddd DD MMMM YYYY")}
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthViewEetdagboek;